import { useState, useEffect } from 'react';
import api from '../../utils/api';

/**
 * Fetches /libraries?limit=50 once on mount.
 * Returns { libraries, totalCount, uniqueCities, totalSeats, topLibraries, loading, error }.
 */
function useLibraries() {
  const [libraries, setLibraries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [uniqueCities, setUniqueCities] = useState(0);
  const [totalSeats, setTotalSeats] = useState(0);
  const [topLibraries, setTopLibraries] = useState([]);
  const [websiteLibraries, setWebsiteLibraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    api
      .get('/libraries', { params: { limit: 50 } })
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || {};
        const libs = data.libraries || [];
        const total = data.pagination?.totalItems ?? libs.length;

        const cities = new Set(libs.map((l) => l.city).filter(Boolean)).size;
        const seats = libs.reduce((sum, l) => sum + (l.capacity || 0), 0);

        const sorted = [...libs].sort((a, b) => {
          const aTop = a.top_rated ? 1 : 0;
          const bTop = b.top_rated ? 1 : 0;
          if (bTop !== aTop) return bTop - aTop;
          const aRating = a.average_overall_rating || 0;
          const bRating = b.average_overall_rating || 0;
          if (bRating !== aRating) return bRating - aRating;
          return (b.total_ratings || 0) - (a.total_ratings || 0);
        });

        const webLibs = libs
          .filter((l) => l.website_enabled === true && typeof l.website_slug === 'string' && l.website_slug.trim().length > 0)
          .slice(0, 6);

        setLibraries(libs);
        setTotalCount(total);
        setUniqueCities(cities);
        setTotalSeats(seats);
        setTopLibraries(sorted.slice(0, 9));
        setWebsiteLibraries(webLibs);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setLibraries([]);
        setTotalCount(0);
        setUniqueCities(0);
        setTotalSeats(0);
        setTopLibraries([]);
        setWebsiteLibraries([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { libraries, totalCount, uniqueCities, totalSeats, topLibraries, websiteLibraries, loading, error };
}

export default useLibraries;
